import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from "react";
import {
  readPersistedWorkspace,
  writePersistedWorkspace,
} from "../utils/workspacePersistence";

type CustomerWorkspaceContextType = {
  selectedCustomerId: string | null;
  setSelectedCustomerId: (id: string | null) => void;
  selectedCustomerName: string | null;
  setSelectedCustomerName: (name: string | null) => void;
};

const CustomerWorkspaceContext = createContext<
  CustomerWorkspaceContextType | undefined
>(undefined);

export const CustomerWorkspaceProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [selectedCustomerId, setSelectedCustomerIdState] = useState<
    string | null
  >(() => readPersistedWorkspace().id);

  const [selectedCustomerName, setSelectedCustomerNameState] = useState<
    string | null
  >(() => readPersistedWorkspace().name);

  useEffect(() => {
    writePersistedWorkspace(selectedCustomerId, selectedCustomerName);
  }, [selectedCustomerId, selectedCustomerName]);

  const setSelectedCustomerId = useCallback((id: string | null) => {
    setSelectedCustomerIdState(id);
  }, []);

  const setSelectedCustomerName = useCallback((name: string | null) => {
    setSelectedCustomerNameState(name);
  }, []);

  return (
    <CustomerWorkspaceContext.Provider
      value={{
        selectedCustomerId,
        setSelectedCustomerId,
        selectedCustomerName,
        setSelectedCustomerName,
      }}
    >
      {children}
    </CustomerWorkspaceContext.Provider>
  );
};

export const useCustomerWorkspace = (): CustomerWorkspaceContextType => {
  const ctx = useContext(CustomerWorkspaceContext);
  if (!ctx) {
    throw new Error(
      "useCustomerWorkspace must be used within CustomerWorkspaceProvider"
    );
  }
  return ctx;
};
